/**
 * Per-tab identity for the single-admin-session guard (singleAdminSession feature flag).
 *
 * sessionStorage is per-tab and survives refresh, so this id identifies "this tab" across
 * reloads without leaking to other tabs the way localStorage would. The one exception is
 * browser tab duplication, which copies sessionStorage — dedupeDuplicatedTabId() closes
 * that hole by asking sibling tabs over a BroadcastChannel whether the id is already in
 * use and regenerating when one answers.
 */

const STORAGE_KEY = 'adminTabSessionId';
const CHANNEL_NAME = 'rspo-admin-tab-ids';

let tabSessionId: string | null = null;

function newId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getAdminTabSessionId(): string {
  if (!tabSessionId) {
    try {
      tabSessionId = sessionStorage.getItem(STORAGE_KEY);
      if (!tabSessionId) {
        tabSessionId = newId();
        sessionStorage.setItem(STORAGE_KEY, tabSessionId);
      }
    } catch {
      // sessionStorage unavailable (privacy mode edge cases) — fall back to in-memory only.
      tabSessionId = tabSessionId ?? newId();
    }
  }
  return tabSessionId;
}

function regenerateAdminTabSessionId(): string {
  tabSessionId = newId();
  try {
    sessionStorage.setItem(STORAGE_KEY, tabSessionId);
  } catch {
    // in-memory id still applies for this tab's lifetime
  }
  return tabSessionId;
}

/**
 * Call once per tab at startup. Keeps a channel open for the tab's lifetime so it can
 * answer future duplicates' pings, and pings siblings so a freshly duplicated tab
 * (which copied our sessionStorage) regenerates its id instead of impersonating us.
 */
export function dedupeDuplicatedTabId(): void {
  if (typeof BroadcastChannel === 'undefined') return;

  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent) => {
    const message = event.data as { type?: string; id?: string } | null;
    if (!message?.id || message.id !== getAdminTabSessionId()) return;

    if (message.type === 'ping') {
      // Another tab booted with our id — tell it the id is taken.
      channel.postMessage({ type: 'taken', id: message.id });
    } else if (message.type === 'taken') {
      // We are the duplicate: an established tab already owns this id.
      regenerateAdminTabSessionId();
    }
  };

  channel.postMessage({ type: 'ping', id: getAdminTabSessionId() });
}
